<template>
  <div v-if="visible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">{{$t('setPassword.title')}}</h2>
      </div>
      
      <div class="modal-body">
        <p class="modal-description">
          {{$t('setPassword.description')}}
        </p>
        
        <div class="form-group">
          <input
            v-model="passwordForm.password"
            type="password"
            :placeholder="$t('setPassword.passwordPlaceholder')"
            class="password-input"
            :class="{ 'error': passwordError }"
          />
        </div>
        
        <div class="password-requirements">
          <p class="requirements-title">{{$t('setPassword.requirementsTitle')}}</p>
          <ul class="requirements-list">
            <li :class="{ 'valid': passwordLengthValid }">{{$t('setPassword.requirementLength')}}</li>
            <li :class="{ 'valid': passwordFormatValid }">{{$t('setPassword.requirementFormat')}}</li>
            <li :class="{ 'valid': passwordMatchValid }">{{$t('setPassword.requirementMatch')}}</li>
          </ul>
        </div>
        
        <div class="form-group">
          <input
            v-model="passwordForm.confirmPassword"
            type="password"
            :placeholder="$t('setPassword.confirmPasswordPlaceholder')"
            class="password-input"
            :class="{ 'error': confirmPasswordError }"
          />
        </div>
        
        <div class="form-group">
          <input
            v-model="passwordForm.email"
            type="email"
            class="email-display"
            readonly
          />
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          class="continue-btn" 
          @click="handleContinue"
          :disabled="!canContinue"
        >
          {{$t('setPassword.continue')}}
        </button>
        <button class="skip-btn" @click="handleSkip">
          {{$t('setPassword.skip')}}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { setPassword } from '@/api/login.js'

export default {
  name: 'SetPasswordModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    email: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      passwordForm: {
        password: '',
        confirmPassword: '',
        email: ''
      },
      passwordError: false,
      confirmPasswordError: false,
      loading: false
    }
  },
  computed: {
    passwordLengthValid() {
      return this.passwordForm.password.length >= 8 && this.passwordForm.password.length <= 20
    },
    passwordFormatValid() {
      const hasLetter = /[a-zA-Z]/.test(this.passwordForm.password)
      const hasNumber = /[0-9]/.test(this.passwordForm.password)
      return hasLetter && hasNumber && this.passwordForm.password.length > 0
    },
    passwordMatchValid() {
      return this.passwordForm.password === this.passwordForm.confirmPassword && 
             this.passwordForm.password.length > 0
    },
    canContinue() {
      return this.passwordLengthValid && 
             this.passwordFormatValid && 
             this.passwordMatchValid &&
             !this.loading
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.passwordForm.email = this.email
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.passwordForm.password = ''
      this.passwordForm.confirmPassword = ''
      this.passwordError = false
      this.confirmPasswordError = false
      this.loading = false
    },
    handleOverlayClick() {
      // 不允许点击遮罩关闭弹窗
    },
    async handleContinue() {
      if (!this.canContinue) return
      
      this.loading = true
      try {
        const res = await setPassword({
          email: this.passwordForm.email,
          password: this.passwordForm.password
        })
        
        if (res && res.code === 200) {
          this.$message.success(this.$t('setPassword.success'))
          // 重新获取用户信息以更新hasPassword状态
          this.$store.dispatch('GetInfo')
          this.$emit('success')
          this.$emit('close')
        } else {
          this.$message.error(res?.msg || this.$t('setPassword.failure'))
        }
      } catch (error) {
        this.$message.error(this.$t('setPassword.failure'))
      } finally {
        this.loading = false
      }
    },
    handleSkip() {
      this.$emit('skip')
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 24px 24px 0;
  text-align: center;
}

.modal-title {
  font-size: 24px;
  font-weight: bold;
  color: #000;
  margin: 0;
}

.modal-body {
  padding: 20px 24px;
}

.modal-description {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 20px 0;
  text-align: center;
  white-space: normal;
  word-break: break-word;
}

.form-group {
  margin-bottom: 16px;
}

.password-input {
  width: 100%;
  height: 48px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 16px;
  background: #f8f8f8;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0e32d9;
    background: white;
  }

  &.error {
    border-color: #ff4d4f;
  }
}

.email-display {
  width: 100%;
  height: 48px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 16px;
  background: #f5f5f5;
  color: #666;
  box-sizing: border-box;
  cursor: not-allowed;
}

.password-requirements {
  margin: 16px 0;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  counter-reset: requirement-counter;
}

.requirements-title {
  font-size: 14px;
  color: #333;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.requirements-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.requirements-list li {
  font-size: 13px;
  color: #666;
  margin: 4px 0;
  position: relative;
  padding-left: 20px;
  counter-increment: requirement-counter;

  &::before {
    content: counter(requirement-counter) '、';
    position: absolute;
    left: 0;
    color: #666;
    font-weight: 500;
  }

  &.valid {
    color: #0e32d9;

    &::before {
      content: counter(requirement-counter) '、';
      color: #0e32d9;
    }
  }
}

.modal-footer {
  padding: 0 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.continue-btn {
  width: 100%;
  height: 48px;
  background: #0e32d9;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #0c2bc7;
  }

  &:disabled {
    background: #d9d9d9;
    cursor: not-allowed;
  }
}

.skip-btn {
  width: 100%;
  height: 40px;
  background: transparent;
  color: #666;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #333;
  }
}

// 移动端适配
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 20px;
    max-width: 95%;
  }

  .modal-header {
    padding: 20px 20px 0;
  }

  .modal-title {
    font-size: 20px;
  }

  .modal-body {
    padding: 16px 20px;
  }

  .modal-footer {
    padding: 0 20px 20px;
  }

  .modal-description {
    font-size: 14px;
    white-space: normal;
    line-height: 1.5;
  }

  .password-input,
  .email-display {
    height: 44px;
    font-size: 14px;
  }

  .continue-btn {
    height: 44px;
    font-size: 14px;
  }
}
</style>
